/* iter92 — Director real-time KPI dashboard.
 * Polls /api/director/dashboard every 30s and paints a live overview:
 *   ▸ Présence du jour (élèves + personnel)
 *   ▸ Encaissements (jour / mois / année) + impayés
 *   ▸ Retenues du mois
 *   ▸ Alertes (notifs échouées, absences en attente, PSP)
 * A single screen the director can leave open in the corner of their office.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import SmsBroadcastDialog from '../components/director/SmsBroadcastDialog';
import SmsHistoryTable from '../components/settings/SmsHistoryTable';
import {
  Users, GraduationCap, Wallet, TrendUp, WarningCircle, Clock,
  ArrowsClockwise, CalendarBlank, Buildings, ArrowUpRight, ForkKnife, Bus,
  ShieldCheck, Cake, Megaphone, ChatText,
} from '@phosphor-icons/react';

const REFRESH_MS = 30_000;

const fmtFCFA = (v) =>
  (Number(v) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })
    .replace(/,/g, ' ') + ' FCFA';

const Ring = ({ pct, color = '#0052FF', label, sub }) => {
  const R = 44;
  const C = 2 * Math.PI * R;
  const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className="relative w-[112px] h-[112px]" data-testid={`ring-${label}`}>
      <svg width="112" height="112" viewBox="0 0 112 112">
        <circle cx="56" cy="56" r={R} stroke="#E2E8F0" strokeWidth="10" fill="none" />
        <circle cx="56" cy="56" r={R} stroke={color} strokeWidth="10" fill="none"
          strokeDasharray={C} strokeDashoffset={C * (1 - clamped / 100)}
          strokeLinecap="round" transform="rotate(-90 56 56)"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-outfit font-bold text-[#0F172A]">
          {clamped.toFixed(0)}%
        </span>
        <span className="text-[10px] text-[#64748B] uppercase tracking-wider">
          {sub}
        </span>
      </div>
    </div>
  );
};

const KpiCard = ({ icon: Icon, tone, label, value, sub, testid }) => (
  <Card className="border border-[#E2E8F0] shadow-none hover:shadow-md transition-shadow"
    data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase tracking-wider">
            <Icon size={14} weight="duotone" style={{ color: tone }} />
            {label}
          </div>
          <div className="mt-1 text-2xl font-outfit font-bold text-[#0F172A]">
            {value}
          </div>
          {sub && <div className="text-[11px] text-[#94A3B8] mt-0.5">{sub}</div>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const AlertPill = ({ tone, count, label, testid }) => (
  <div
    data-testid={testid}
    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium
      ${tone === 'red' ? 'bg-red-50 text-red-700 border border-red-200'
        : tone === 'amber' ? 'bg-amber-50 text-amber-700 border border-amber-200'
          : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
    <WarningCircle size={13} weight="duotone" />
    <span className="font-bold">{count}</span>
    <span>{label}</span>
  </div>
);

const DirectorKpi = () => {
  const { language } = useLanguage();
  const isFR = language === 'fr';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const timerRef = useRef(null);
  const { user } = useAuth();
  const canManageSms = ['admin', 'director_studies'].includes(user?.role);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);

  // iter100 — Custom birthday message modal state.
  const [bdayModal, setBdayModal] = useState(null); // { kind, id, name }
  const [bdayMsg, setBdayMsg] = useState('');
  const [bdaySending, setBdaySending] = useState(false);

  const openBdayModal = (kind, id, name) => {
    const tpl = isFR
      ? "Joyeux anniversaire {first_name} ! Toute l'équipe de l'école te souhaite une merveilleuse journée. {signature}"
      : "Happy birthday {first_name}! The whole school team wishes you a wonderful day. {signature}";
    setBdayModal({ kind, id, name });
    setBdayMsg(tpl);
  };
  const closeBdayModal = () => { setBdayModal(null); setBdayMsg(''); };
  const sendBdayMsg = async () => {
    if (!bdayModal || !bdayMsg.trim()) return;
    setBdaySending(true);
    try {
      const { data: r } = await api.post(
        '/dashboard/birthdays/custom-message',
        { target_kind: bdayModal.kind, target_id: bdayModal.id,
          message: bdayMsg.trim() });
      if (r.sent) {
        toast.success(isFR
          ? `SMS envoyé à ${bdayModal.name}` : `SMS sent to ${bdayModal.name}`);
        closeBdayModal();
      } else {
        toast.error(r.error || (isFR ? 'Échec de l\'envoi' : 'Send failed'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || (isFR ? 'Erreur d\'envoi' : 'Send error'));
    } finally { setBdaySending(false); }
  };

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const { data } = await api.get('/director/dashboard');
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || (isFR ? 'Erreur chargement dashboard' : 'Dashboard error'));
    } finally { setLoading(false); }
  }, [isFR]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    timerRef.current = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [autoRefresh, load]);

  if (loading && !data) {
    return (
      <div className="p-6" data-testid="director-kpi-loading">
        <div className="animate-pulse text-[#94A3B8]">
          {isFR ? 'Chargement du tableau de bord…' : 'Loading dashboard…'}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const refreshedAt = new Date(data.refreshed_at).toLocaleTimeString('fr-FR');
  const stud = data.attendance.students;
  const staff = data.attendance.staff;
  const allClear = data.deductions.pending_absences === 0
    && data.alerts.notif_failed_24h === 0
    && data.support_plans.pending_review === 0
    && data.finance.families_late === 0;

  return (
    <div className="p-6 space-y-4" data-testid="director-kpi-dashboard">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-bold flex items-center gap-2">
            <Buildings size={26} className="text-[#0052FF]" weight="duotone" />
            {isFR ? 'Tableau de bord Directeur' : 'Director dashboard'}
          </h1>
          <p className="text-sm text-[#64748B] mt-1 flex items-center gap-2 flex-wrap">
            <CalendarBlank size={14} />
            {isFR ? 'Vue temps réel' : 'Real-time view'}
            <span className="text-[#CBD5E1]">·</span>
            {isFR ? 'Rafraîchi à' : 'Refreshed at'} <b>{refreshedAt}</b>
            <span className="text-[#CBD5E1]">·</span>
            {data.period.today}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManageSms && (
            <Button size="sm" onClick={() => setBroadcastOpen(true)}
              data-testid="open-broadcast-btn"
              className="bg-[#0052FF] hover:bg-[#0041CC] text-white">
              <Megaphone size={14} className="mr-1.5" weight="duotone" />
              {isFR ? 'Message groupé' : 'Group message'}
            </Button>
          )}
          <Button variant="outline" size="sm"
            onClick={() => setJournalOpen(true)}
            data-testid="sms-journal-link"
            className="border-[#E2E8F0]">
            <ChatText size={14} className="mr-1.5" />
            {isFR ? 'Journal SMS' : 'SMS log'}
          </Button>
          <label className="flex items-center gap-1.5 text-xs text-[#64748B] cursor-pointer"
            data-testid="auto-refresh-toggle">
            <input type="checkbox" checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)} />
            {isFR ? 'Auto (30s)' : 'Auto (30s)'}
          </label>
          <Button variant="outline" size="sm" onClick={() => load()}
            data-testid="manual-refresh-btn"
            className="border-[#E2E8F0]">
            <ArrowsClockwise size={14} className="mr-1.5" />
            {isFR ? 'Actualiser' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* ── Row 1 — Overview counts ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={GraduationCap} tone="#0052FF"
          label={isFR ? 'Élèves actifs' : 'Active students'}
          value={data.overview.students_active}
          sub={isFR ? 'Tous cycles confondus' : 'All cycles'}
          testid="kpi-students" />
        <KpiCard icon={Users} tone="#059669"
          label={isFR ? 'Enseignants' : 'Teachers'}
          value={data.overview.teachers}
          testid="kpi-teachers" />
        <KpiCard icon={Users} tone="#7C3AED"
          label={isFR ? 'Personnel' : 'Staff'}
          value={data.overview.staff}
          sub={isFR ? 'Cantine, transport, sécurité…' : 'Canteen, transport, security…'}
          testid="kpi-staff" />
        <KpiCard icon={TrendUp} tone="#B91C1C"
          label={isFR ? "Encaissé aujourd'hui" : 'Collected today'}
          value={fmtFCFA(data.finance.today_collected)}
          testid="kpi-today-collected" />
      </div>

      {/* ── Row 2 — Attendance rings + alerts ── */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit font-semibold text-base flex items-center gap-2">
              <Clock size={18} className="text-[#0052FF]" weight="duotone" />
              {isFR ? 'Présence du jour' : "Today's presence"}
            </h2>
            <span className="text-[11px] text-[#94A3B8]">
              {isFR ? 'Mis à jour à chaque pointage' : 'Live via time-punch'}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-center">
            <div className="flex flex-col items-center gap-1"
              data-testid="attendance-students">
              <Ring pct={stud.rate_pct} color="#0052FF" label="students"
                sub={isFR ? 'Élèves' : 'Students'} />
              <div className="text-center mt-2">
                <div className="text-sm font-semibold text-[#0F172A]">
                  {stud.present} / {data.overview.students_active}
                </div>
                <div className="text-[11px] text-[#94A3B8]">
                  {stud.absent} {isFR ? 'absent(s)' : 'absent'}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-1"
              data-testid="attendance-staff">
              <Ring pct={staff.rate_pct} color="#059669" label="staff"
                sub={isFR ? 'Personnel' : 'Staff'} />
              <div className="text-center mt-2">
                <div className="text-sm font-semibold text-[#0F172A]">
                  {staff.present} / {staff.total}
                </div>
                <div className="text-[11px] text-[#94A3B8]">
                  {isFR ? 'Enseignants + Personnel' : 'Teachers + Staff'}
                </div>
              </div>
            </div>
            <div className="md:col-span-2 space-y-2"
              data-testid="alerts-container">
              {data.deductions.pending_absences > 0 && (
                <AlertPill tone="amber"
                  count={data.deductions.pending_absences}
                  label={isFR ? 'absence(s) personnel en attente RH ce mois' : 'staff absences pending HR review this month'}
                  testid="pill-pending-absences" />
              )}
              {data.alerts.notif_failed_24h > 0 && (
                <AlertPill tone="red"
                  count={data.alerts.notif_failed_24h}
                  label={isFR ? 'notification(s) échouée(s) sur 24h' : 'failed notifications in the last 24h'}
                  testid="pill-notif-failed" />
              )}
              {data.support_plans.pending_review > 0 && (
                <AlertPill tone="blue"
                  count={data.support_plans.pending_review}
                  label={isFR ? 'PSP en attente de revue' : 'PSP awaiting review'}
                  testid="pill-psp-pending" />
              )}
              {data.finance.families_late > 0 && (
                <AlertPill tone="amber"
                  count={data.finance.families_late}
                  label={isFR ? 'famille(s) avec solde impayé' : 'families with outstanding balance'}
                  testid="pill-families-late" />
              )}
              {allClear && (
                <div className="text-sm text-emerald-700 font-medium"
                  data-testid="pill-all-clear">
                  ✅ {isFR ? 'Aucune alerte active' : 'No active alert'}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Row 3 — Finance ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="border border-[#E2E8F0] shadow-none"
          data-testid="finance-card-month">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase tracking-wider">
              <Wallet size={14} className="text-emerald-600" weight="duotone" />
              {isFR ? 'Encaissé ce mois' : 'Collected this month'}
            </div>
            <div className="mt-1 text-2xl font-outfit font-bold text-emerald-700">
              {fmtFCFA(data.finance.month_collected)}
            </div>
            <div className="text-[11px] text-[#94A3B8] mt-0.5">
              {data.period.month_first} → {data.period.month_last}
            </div>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0] shadow-none"
          data-testid="finance-card-year">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase tracking-wider">
              <ArrowUpRight size={14} className="text-[#0052FF]" weight="duotone" />
              {isFR ? 'Cumul annuel' : 'Year-to-date'}
            </div>
            <div className="mt-1 text-2xl font-outfit font-bold text-[#0052FF]">
              {fmtFCFA(data.finance.year_collected)}
            </div>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0] shadow-none"
          data-testid="finance-card-outstanding">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase tracking-wider">
              <WarningCircle size={14} className="text-red-600" weight="duotone" />
              {isFR ? 'Impayés cumulés' : 'Outstanding balances'}
            </div>
            <div className="mt-1 text-2xl font-outfit font-bold text-red-700">
              {fmtFCFA(data.finance.outstanding_balance)}
            </div>
            <div className="text-[11px] text-[#94A3B8] mt-0.5">
              {data.finance.families_late} {isFR ? 'familles concernées' : 'families concerned'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Row 4 — Deductions & PSP ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="border border-[#E2E8F0] shadow-none"
          data-testid="deductions-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase tracking-wider">
              <WarningCircle size={14} className="text-red-600" weight="duotone" />
              {isFR ? 'Retenues salariales ce mois' : 'Payroll deductions this month'}
            </div>
            <div className="mt-1 grid grid-cols-3 gap-2">
              <div>
                <div className="text-xs text-[#94A3B8]">
                  {isFR ? 'Nombre' : 'Count'}
                </div>
                <div className="text-xl font-outfit font-bold text-[#0F172A]">
                  {data.deductions.count}
                </div>
              </div>
              <div>
                <div className="text-xs text-[#94A3B8]">
                  {isFR ? 'Heures' : 'Hours'}
                </div>
                <div className="text-xl font-outfit font-bold text-[#0F172A]">
                  {data.deductions.hours}h
                </div>
              </div>
              <div>
                <div className="text-xs text-[#94A3B8]">FCFA</div>
                <div className="text-xl font-outfit font-bold text-red-700">
                  {fmtFCFA(data.deductions.amount_fcfa)}
                </div>
              </div>
            </div>
            <div className="text-[11px] text-[#94A3B8] mt-2">
              {isFR
                ? `${data.deductions.pending_absences} absence(s) en attente de justification`
                : `${data.deductions.pending_absences} absences pending justification`}
            </div>
          </CardContent>
        </Card>
        {/* iter93 — Canteen live widget */}
        {data.canteen && (
          <Card className="border border-[#E2E8F0] shadow-none"
            data-testid="canteen-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase tracking-wider">
                <ForkKnife size={14} className="text-orange-600" weight="duotone" />
                {isFR ? 'Cantine — repas servis aujourd\'hui' : 'Canteen — meals served today'}
              </div>
              <div className="mt-1 flex items-end gap-3">
                <div>
                  <div className="text-3xl font-outfit font-bold text-orange-700">
                    {data.canteen.meals_today}
                  </div>
                  <div className="text-[11px] text-[#94A3B8]">
                    / {data.canteen.enrolled} {isFR ? 'inscrits' : 'enrolled'}
                    {' · '}
                    <span className="font-semibold text-orange-700">
                      {data.canteen.rate_pct}%
                    </span>
                  </div>
                </div>
                <div className="flex-1 h-2 bg-orange-100 rounded-full overflow-hidden ml-2">
                  <div className="h-full bg-orange-500 transition-all"
                    style={{ width: `${Math.min(100, data.canteen.rate_pct)}%` }} />
                </div>
              </div>
              <div className="text-[11px] text-[#94A3B8] mt-3 flex justify-between">
                <span>
                  {isFR ? 'Moyenne 7 derniers jours' : 'Rolling 7-day average'} :
                </span>
                <span className="font-semibold text-[#475569]">
                  {data.canteen.avg_last_7d} {isFR ? 'repas / jour' : 'meals / day'}
                </span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Row 4bis — Transport widget (iter94) ── */}
      {data.transport && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card className="border border-[#E2E8F0] shadow-none"
            data-testid="transport-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase tracking-wider">
                <Bus size={14} className="text-cyan-600" weight="duotone" />
                {isFR ? 'Transport — scans du jour' : 'Transport — today\'s scans'}
              </div>
              <div className="mt-2 grid grid-cols-4 gap-2">
                <div>
                  <div className="text-[10px] text-[#94A3B8] uppercase">
                    {isFR ? 'Montées' : 'Boarded'}
                  </div>
                  <div className="text-xl font-outfit font-bold text-cyan-700">
                    ↑ {data.transport.boarded}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-[#94A3B8] uppercase">
                    {isFR ? 'Descentes' : 'Alighted'}
                  </div>
                  <div className="text-xl font-outfit font-bold text-cyan-700">
                    ↓ {data.transport.alighted}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-[#94A3B8] uppercase"
                    title={isFR ? 'Enfants montés mais non encore redescendus' : 'Boarded but not yet alighted'}>
                    {isFR ? 'À bord' : 'On bus'}
                  </div>
                  <div className={`text-xl font-outfit font-bold ${data.transport.on_bus > 0 ? 'text-amber-600' : 'text-emerald-700'}`}>
                    {data.transport.on_bus}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-[#94A3B8] uppercase">
                    {isFR ? 'Lignes' : 'Routes'}
                  </div>
                  <div className="text-xl font-outfit font-bold text-[#0F172A]">
                    {data.transport.active_routes}
                  </div>
                </div>
              </div>
              <div className="text-[11px] text-[#94A3B8] mt-3 flex justify-between items-center">
                <span>
                  {isFR ? 'Taux d\'utilisation' : 'Usage rate'} :
                  <b className="text-cyan-700 ml-1">{data.transport.rate_pct}%</b>
                  <span className="text-[#CBD5E1] mx-1">·</span>
                  {data.transport.enrolled} {isFR ? 'inscrits' : 'enrolled'}
                </span>
                <div className="w-24 h-1.5 bg-cyan-100 rounded-full overflow-hidden">
                  <div className="h-full bg-cyan-500 transition-all"
                    style={{ width: `${Math.min(100, data.transport.rate_pct)}%` }} />
                </div>
              </div>
            </CardContent>
          </Card>
          {/* iter95 — Security widget */}
          {data.security && (
            <Card className={`border shadow-none ${data.security.incidents_high > 0 ? 'border-red-300 bg-red-50/30' : 'border-[#E2E8F0]'}`}
              data-testid="security-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase tracking-wider">
                  <ShieldCheck size={14} className={data.security.incidents_high > 0 ? 'text-red-600' : 'text-indigo-600'}
                    weight="duotone" />
                  {isFR ? 'Sécurité — journée' : 'Security — today'}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] text-[#94A3B8] uppercase">
                      {isFR ? 'Visiteurs' : 'Visitors'}
                    </div>
                    <div className="text-xl font-outfit font-bold text-indigo-700">
                      {data.security.visitors_today}
                    </div>
                    <div className="text-[11px] text-[#94A3B8]">
                      {data.security.visitors_inside > 0
                        ? `🟢 ${data.security.visitors_inside} ${isFR ? 'à l\'intérieur' : 'inside'}`
                        : (isFR ? 'Personne à l\'intérieur' : 'Nobody inside')}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#94A3B8] uppercase">
                      {isFR ? 'Incidents' : 'Incidents'}
                    </div>
                    <div className={`text-xl font-outfit font-bold ${data.security.incidents_high > 0 ? 'text-red-700' : 'text-indigo-700'}`}>
                      {data.security.incidents_today}
                    </div>
                    <div className="text-[11px] text-[#94A3B8]">
                      {data.security.incidents_high > 0
                        ? <span className="text-red-700 font-bold">
                            🚨 {data.security.incidents_high} {isFR ? 'grave(s)' : 'high'}
                          </span>
                        : (data.security.incidents_open > 0
                          ? `${data.security.incidents_open} ${isFR ? 'en cours' : 'open'}`
                          : (isFR ? 'Aucun ouvert' : 'None open'))}
                    </div>
                  </div>
                </div>
                <a href="/security"
                  className="text-[11px] text-[#0052FF] hover:underline mt-3 inline-block"
                  data-testid="security-open-page">
                  {isFR ? 'Ouvrir la page Sécurité →' : 'Open Security page →'}
                </a>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* iter99/100 — Birthdays widget (students + staff/teachers) */}
      {data.birthdays && (
        data.birthdays.count_today > 0 || data.birthdays.count_week > 0 ||
        data.birthdays.count_staff_today > 0 || data.birthdays.count_staff_week > 0
      ) && (
        <Card className="border-2 border-pink-200 shadow-none bg-gradient-to-r from-pink-50 to-purple-50"
          data-testid="birthday-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-pink-700 text-xs uppercase tracking-wider font-bold">
              <Cake size={16} weight="duotone" />
              {isFR ? 'Anniversaires' : 'Birthdays'}
            </div>

            {/* Students today */}
            {data.birthdays.count_today > 0 && (
              <div className="mt-2">
                <div className="text-xs font-semibold text-pink-800 mb-1">
                  🎉 {isFR ? "Élèves aujourd'hui" : 'Students today'} ({data.birthdays.count_today})
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.birthdays.today.map(b => (
                    <div key={b.id}
                      data-testid={`bday-today-${b.id}`}
                      className="inline-flex items-center gap-1.5 bg-white/70 rounded-full pl-1 pr-2 py-0.5 border border-pink-200">
                      <span className="w-5 h-5 rounded-full bg-pink-500 text-white text-[10px] font-bold flex items-center justify-center">
                        {b.gender === 'F' ? '♀' : '♂'}
                      </span>
                      <span className="text-xs font-semibold text-[#0F172A]">{b.name}</span>
                      <span className="text-[10px] text-[#94A3B8]">{b.class}</span>
                      <button
                        type="button"
                        onClick={() => openBdayModal('student', b.id, b.name)}
                        data-testid={`bday-send-student-${b.id}`}
                        className="ml-1 rounded-full bg-pink-600 hover:bg-pink-700 text-white text-[10px] font-semibold px-2 py-0.5">
                        {isFR ? '✉ SMS' : '✉ SMS'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Staff today */}
            {data.birthdays.count_staff_today > 0 && (
              <div className="mt-3">
                <div className="text-xs font-semibold text-purple-800 mb-1">
                  🎂 {isFR ? "Personnel aujourd'hui" : 'Staff today'} ({data.birthdays.count_staff_today})
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.birthdays.staff_today.map(b => (
                    <div key={`${b.kind}-${b.id}`}
                      data-testid={`bday-staff-today-${b.id}`}
                      className="inline-flex items-center gap-1.5 bg-white/70 rounded-full pl-2 pr-2 py-0.5 border border-purple-200">
                      <span className="text-[10px] uppercase font-bold text-purple-700">
                        {b.kind === 'teacher' ? (isFR ? 'Ens.' : 'Tea.') : (isFR ? 'Perso.' : 'Staff')}
                      </span>
                      <span className="text-xs font-semibold text-[#0F172A]">{b.name}</span>
                      {b.role && <span className="text-[10px] text-[#94A3B8]">{b.role}</span>}
                      <button
                        type="button"
                        onClick={() => openBdayModal(b.kind, b.id, b.name)}
                        data-testid={`bday-send-staff-${b.id}`}
                        className="ml-1 rounded-full bg-purple-600 hover:bg-purple-700 text-white text-[10px] font-semibold px-2 py-0.5">
                        {isFR ? '✉ SMS' : '✉ SMS'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Week horizon (students + staff) */}
            {(data.birthdays.count_week > 0 || data.birthdays.count_staff_week > 0) && (
              <div className="mt-3 text-[11px] text-[#475569]">
                <b>{isFR ? 'Cette semaine :' : 'This week:'}</b>{' '}
                {[
                  ...(data.birthdays.week || []).map(b =>
                    `${b.name} (${b.class}, J+${b.days_until})`),
                  ...(data.birthdays.staff_week || []).map(b =>
                    `${b.name} (${b.kind === 'teacher' ? (isFR?'Ens.':'Tea.') : (isFR?'Perso.':'Staff')}, J+${b.days_until})`),
                ].join(' · ')}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* iter101 — Segmented broadcast dialog + SMS journal */}
      <SmsBroadcastDialog open={broadcastOpen} onOpenChange={setBroadcastOpen} isFR={isFR} />
      <Dialog open={journalOpen} onOpenChange={setJournalOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="sms-journal-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">{isFR ? 'Journal des SMS' : 'SMS log'}</DialogTitle>
          </DialogHeader>
          <SmsHistoryTable canResend={canManageSms} limit={50} />
        </DialogContent>
      </Dialog>

      {/* iter100 — Custom birthday message modal */}
      {bdayModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="bday-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5">
            <div className="flex items-center gap-2 mb-2">
              <Cake size={18} weight="duotone" className="text-pink-600" />
              <h3 className="text-base font-bold text-[#0F172A]">
                {isFR ? 'Message personnalisé' : 'Custom message'}
              </h3>
            </div>
            <div className="text-sm text-[#475569] mb-2">
              {isFR ? 'Envoyer à' : 'Send to'}{' '}
              <b>{bdayModal.name}</b>{' '}
              <span className="text-xs text-[#94A3B8]">({bdayModal.kind})</span>
            </div>
            <textarea
              value={bdayMsg}
              onChange={e => setBdayMsg(e.target.value.slice(0, 320))}
              rows={5}
              placeholder={isFR ? 'Votre message…' : 'Your message…'}
              data-testid="bday-modal-textarea"
              className="w-full rounded-md border border-[#E2E8F0] p-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400" />
            <div className="mt-1 flex items-center justify-between text-[11px] text-[#94A3B8]">
              <span>
                {isFR ? 'Variables :' : 'Variables:'}{' '}
                <code className="bg-slate-100 px-1 rounded">{'{first_name}'}</code>{' '}
                <code className="bg-slate-100 px-1 rounded">{'{last_name}'}</code>{' '}
                <code className="bg-slate-100 px-1 rounded">{'{school_name}'}</code>{' '}
                <code className="bg-slate-100 px-1 rounded">{'{signature}'}</code>
              </span>
              <span>{bdayMsg.length}/320</span>
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={closeBdayModal}
                data-testid="bday-modal-cancel" disabled={bdaySending}>
                {isFR ? 'Annuler' : 'Cancel'}
              </Button>
              <Button
                onClick={sendBdayMsg}
                disabled={bdaySending || !bdayMsg.trim()}
                data-testid="bday-modal-send"
                className="bg-pink-600 hover:bg-pink-700 text-white">
                {bdaySending
                  ? (isFR ? 'Envoi…' : 'Sending…')
                  : (isFR ? 'Envoyer le SMS' : 'Send SMS')}
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* ── Row 5 — PSP alone ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="border border-[#E2E8F0] shadow-none"
          data-testid="psp-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase tracking-wider">
              <TrendUp size={14} className="text-[#7C3AED]" weight="duotone" />
              {isFR ? 'Plans de Soutien Pédagogique' : 'Pedagogical Support Plans'}
            </div>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <div>
                <div className="text-xs text-[#94A3B8]">
                  {isFR ? 'Actifs' : 'Active'}
                </div>
                <div className="text-2xl font-outfit font-bold text-[#7C3AED]">
                  {data.support_plans.active}
                </div>
              </div>
              <div>
                <div className="text-xs text-[#94A3B8]">
                  {isFR ? 'En attente revue' : 'Pending review'}
                </div>
                <div className="text-2xl font-outfit font-bold text-amber-600">
                  {data.support_plans.pending_review}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DirectorKpi;
